import { create } from 'zustand';
import { message } from 'antd';
import axiosInstance from '../utils/axiosInstance';

const useDumpStore = create((set) => ({
  dumps: [],
  loading: false,
  error: null,

  fetchDumps: async () => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get('/dumps/');
      set({ 
        dumps: data.map((dump, index) => ({ ...dump, key: index })),
        loading: false,
        error: null 
      });
    } catch (error) {
      set({ error: error.message, loading: false });
      message.error('Failed to fetch dumps');
    }
  },

  createDump: async () => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.post('/dumps/');
      message.success('Database dump created successfully');
      // Refresh the dumps list after creating new dump
      await useDumpStore.getState().fetchDumps();
      return { success: true, data };
    } catch (error) {
      set({ loading: false });
      message.error('Failed to create dump');
      return { success: false, error };
    }
  },

  downloadDump: async (filename) => {
    try {
      const response = await axiosInstance.get(`/dumps/download/${filename}/`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      // Append to html page
      document.body.appendChild(link);
      // Start download
      link.click();
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Failed to download dump');
    }
  },

  deleteDump: async (filename) => {
    try {
      await axiosInstance.delete(`/dumps/${filename}/`);
      message.success('Dump deleted successfully');
      // Refresh the dumps list
      await useDumpStore.getState().fetchDumps();
      return { success: true };
    } catch (error) {
      message.error('Failed to delete dump');
      return { success: false, error };
    }
  },

  restoreDatabase: async (fileOrFilename) => {
    try {
      let response;
      
      if (typeof fileOrFilename === 'string') {
        // If string, it's a filename of existing dump
        response = await axiosInstance.post('/dumps/restore/', { filename: fileOrFilename });
      } else {
        // If not string, it's a file object
        const formData = new FormData();
        formData.append('file', fileOrFilename);
        response = await axiosInstance.post('/dumps/restore/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      
      message.success('Database restored successfully');
      return { success: true };
    } catch (error) {
      message.error('Failed to restore database');
      return { success: false, error };
    }
  }
}));

export default useDumpStore;
